/* eslint-disable no-alert -- TODO: replace alert with a modal */
/**
 * Tools and scripts needed to run on translate.khanacademy.org
 *
 * Translate.khanacademy.org is the place where translators can go that loads
 * Crowdin's just-in-place-translation (JIPT) service. It allows them to edit
 * our strings directly on our site.
 *
 * This file loads the scripts that need to be run to load jipt on evey page
 * and has some functions necessary to encode and decode crowdin's strings.
 */
import debounce from "lodash.debounce";
import _ from "underscore";

import {khanFetch} from "@khan/data-fetch";
import Log, {Errors, KAError} from "@khan/logging";
import {Deferred} from "@khan/util-deferred";

import * as JiptHack from "./jipt-hack.ts";
import JIPT from "./jipt.ts";
import jipt from "./jipt/jipt.js";
import {
    createLintTranslationInput,
    type TranslationLinterInput,
} from "./lint_queries.ts";

interface Phrase {
    text: ReadonlyArray<string>;
    translation: ReadonlyArray<string>;
    id: string | number;
    context?: string | null | undefined;
    elements?: any | null | undefined;
}

interface WrappedPhrase {
    data: {
        text: ReadonlyArray<string>;
        translation: ReadonlyArray<string>;
        id: string | number;
        context?: string | null | undefined;
        elements?: any | null | undefined;
    };
}

interface Suggestion {
    phraseId: string | null | undefined;
    text: string;
}

interface LintOk {
    status: "ok";
    correction?: string | null | undefined;
}

export interface LintError {
    status: "error";
    message: string;
    correction?: string | null | undefined;
}

export type LintResult = LintOk | LintError;

export interface Translation {
    deferred: Deferred;
    lang: string;
    msgid: string;
    context: string;
}

interface LintChangeData {
    isEscaped: boolean;
    lintResult: any;
    phrase: any;
}

/**
 * The only escape sequences that we recognize.
 */
const ESCAPE_SEQUENCES = {
    "\n": "\\n",
    "\r": "\\r",
    "\t": "\\t",
    "\\": "\\\\",
} as const;

/**
 * We expect each line to start with with '#.' whereas crowdin returns context
 * as tcomments -- that is, with lines that either start with just '#', or
 * with no prefix.
 */
function tcommentToComment(str: string): string {
    return str.replace(/^#?\s*/gm, "#. ");
}

function escapeDoubleColon(str: string): string {
    return str.replace("::", "::::");
}

function getPhraseKey(phrase?: Phrase | null) {
    if (!phrase) {
        return "";
    }
    const texts = JSON.stringify(phrase.text);
    const translations = JSON.stringify(phrase.translation);
    return `${escapeDoubleColon(texts)}::${escapeDoubleColon(
        phrase.id.toString(),
    )}::${escapeDoubleColon(translations)}`;
}

let lastDirtyPhraseKey = "";
let lastCleanPhraseKey = "";

/**
 * Interface to JIPT.
 *
 * All direct interaction with JIPT internals should be done here without
 * leaking implementation details (such as data formats) from jipt.js.
 *
 * We host a modified version of jipt.js, and want to modify that file as
 * little as possible. For more complex interactions with jipt, we put the
 * wrapper code here instead of there. We want interaction with the jipt
 * variable to be centralized to this file so that if we need to upgrade our
 * copy of jipt.js, we only need to make the few changes that were made there,
 * and then ensure that the functionality in this file is still supported
 * instead of needing to search around the codebase for any reference to the
 * jipt variable.
 *
 * See: third_party/javascript-khansrc/jipt.js
 */
const JiptInterface = {
    /**
     * Return the translation status of the given jiptString.
     * Status will be one of "untranslated", "partially_translated",
     * "translated", "partially_approved", "approved", "hidden", or
     * "not_found".
     *
     * If status hasn't been determined yet because the string hasn't been
     * loaded, returns "loading".
     *
     * @param {string} jiptString The JIPT string to check status of.
     *      e.g. "crwdns3217892:0crwdne3217892:0".
     */
    getTranslationStatus: function (jiptString: string): string {
        const phraseId = JiptInterface.jiptStringToPhraseId(jiptString);
        if (!phraseId) {
            return "not_found";
        }
        const phrase = jipt.phrases[phraseId];
        if (!phrase) {
            // TODO(jlfwong): Start loading the phrase in this case?
            return "loading";
        }
        return jipt.get_phrase_status(phrase.data.status);
    },

    /**
     * Gets a phrase by jiptString or phrase id.
     */
    getPhrase: function (
        jiptStringOrPhraseId?: string | null,
    ): Phrase | null | undefined {
        if (jipt.phrases[jiptStringOrPhraseId]) {
            return jipt.phrases[jiptStringOrPhraseId].data;
        }

        const phraseId =
            JiptInterface.jiptStringToPhraseId(jiptStringOrPhraseId);

        if (phraseId) {
            return jipt.phrases[phraseId]?.data;
        }

        return undefined;
    },

    /**
     * Returns the Crowdin phrase ID of the phrase ID currently being edited,
     * e.g. "3173741".
     *
     * Returns null if no phrase is selected.
     */
    getActivePhraseId: function (): string | null | undefined {
        return (
            (jipt.editor &&
                jipt.editor.currentTranslation &&
                jipt.editor.currentTranslation.id) ||
            null
        );
    },

    getDirtyPhrase: function (): Phrase | null | undefined {
        const activePhraseId = JiptInterface.getActivePhraseId();
        const activePhrase = JiptInterface.getPhrase(activePhraseId);
        if (!jipt.translation_preview.id) {
            return activePhrase;
        }
        if (activePhraseId !== jipt.translation_preview.id) {
            throw new KAError(
                "Preview phrase does not match active phrase",
                Errors.Internal,
            );
        }

        // Phrase could be null here
        // @ts-expect-error - TS2322 - Type '{ translation: any[]; text?: readonly string[] | undefined; id?: string | number | undefined; context?: string | null | undefined; elements?: any; }' is not assignable to type 'Phrase'.
        return {
            ...activePhrase,
            translation: [jipt.translation_preview.value],
        };
    },

    getCleanPhrase: function (): Phrase | null | undefined {
        const activePhraseId = JiptInterface.getActivePhraseId();
        return JiptInterface.getPhrase(activePhraseId);
    },

    _initChangeEventEmitter: _.once(function () {
        const orig = jipt.update_phrase_dom_highlight.bind(jipt);
        jipt.update_phrase_dom_highlight = function () {
            const event = new Event("ka_jipt_status_change");
            window.dispatchEvent(event);
            orig();
        };
    }) as () => void,

    /**
     * Add an event listener that fires whenever the status of string changes.
     *
     * @param {function} cb The function to invoke whenever the selection
     *      changes.
     */
    addStatusChangeListener: function (cb: (...args: Array<any>) => unknown) {
        JiptInterface._initChangeEventEmitter();
        window.addEventListener("ka_jipt_status_change", cb);
    },

    /**
     * The opposite of addSelectionChangeEventListener.
     */
    removeStatusChangeListener: function (
        cb: (...args: Array<any>) => unknown,
    ) {
        window.removeEventListener("ka_jipt_status_change", cb);
    },

    addLintChangeListener: function (
        cb: (ev: Event, data: LintChangeData) => unknown,
    ) {
        window.addEventListener("ka_lint_change", (ev) => {
            cb(ev, (ev as CustomEvent<LintChangeData>).detail);
        });
    },

    removeLintChangeListener: function (cb: (...args: Array<any>) => unknown) {
        window.removeEventListener("ka_lint_change", cb);
    },

    _handleNewTranslation: function ({
        lang,
        msgid,
        context,
    }: {
        lang: string;
        msgid: string;
        context: string;
    }) {
        const deferred = new Deferred();

        const event = new CustomEvent("ka_jipt_new_translation", {
            detail: {
                deferred: deferred,
                lang: lang,
                msgid: msgid,
                context: context,
            },
        });
        window.dispatchEvent(event);
        deferred.resolve();
    },

    /**
     * Add an event listener that fires whenever a new translation gets sent.
     *
     * @param {function} cb The function to invoke whenever the selection
     *      changes.  The callback will be triggered with the following
     *      arguments:
     *          deferred: A deferred of the mark_state api call
     *          lang: The language the translation was added from
     *          msgid: The msgid that a new translation was just added for.
     *          context: The context of the poentry that a new translation was
     *              added for.
     */
    addNewTranslationListener: function (
        cb: (ev: Event, context: Translation) => unknown,
    ) {
        window.addEventListener("ka_jipt_new_translation", (ev) => {
            cb(ev, (ev as CustomEvent<Translation>).detail);
        });
    },

    /**
     * The opposite of addNewTranslationListener.
     */
    removeNewTranslationListener: function (
        cb: (...args: Array<any>) => unknown,
    ) {
        window.removeEventListener("ka_jipt_new_translation", cb);
    },

    /**
     * Return a promise that resolves when phrase data is loaded.
     *
     * @param {Array<string>} jiptStrings The JIPT strings to wait on.
     *      wait on, e.g. ["crwdns3217892:0crwdne3217892:0", ...].
     */
    waitForPhrases: function (
        jiptStrings: ReadonlyArray<string>,
    ): Promise<undefined> {
        const deferred = new Deferred<undefined, undefined>();

        const joinedJiptStrings = jiptStrings.join("\n");

        // If all the strings are already loaded, the callback passed to
        // _load_phrases_delayed doesn't fire, so we need to handle that case
        // specifically.
        const phraseIds = jipt.get_phrases_from_text(joinedJiptStrings);

        if (Object.keys(phraseIds).every((id) => jipt.phrases[id])) {
            deferred.resolve();
            return deferred.promise;
        }

        jipt._load_phrases_delayed(joinedJiptStrings, () => {
            deferred.resolve();
        });

        return deferred.promise;
    },

    /**
     * Focus the JIPT popup editor on the given string on the given element.
     */
    focusEditor: function (
        phraseId: string | null | undefined | number | null | undefined,
        element: any,
    ) {
        if (!jipt.phrases[phraseId]) {
            throw new KAError(
                `Could not find JIPT phrase with ID ${phraseId ?? "(null)"}`,
                Errors.Internal,
            );
        }
        jipt.editorDialog.jiptDialog({action: "open"});
        jipt.show_editor(jipt.phrases[phraseId]?.data, element);
    },

    /**
     * Hide the JIPT popup editor.
     */
    hideEditor: function () {
        jipt.editorDialog && jipt.editorDialog.jiptDialog({action: "close"});
    },

    /**
     * Convert a JIPT string like crwdns3217892:0crwdne3217892:0 to a Crowdin
     * phrase ID like "3217892".
     *
     * If the jiptString provided does not contain a Crowdin phrase ID, returns
     * null.
     */
    jiptStringToPhraseId: function (
        jiptString?: string | null,
    ): string | null | undefined {
        const match = jipt.regexp_new.startPhrase.exec(jiptString);
        if (!match) {
            return null;
        }
        return match[1];
    },

    // JIPT needs to know whether it's in the WYSIWYG mode
    // (translate.khanacademy.org) or in the Translation Editor (Manticore)
    // at www.ka.org/translations/edit/lang/...
    // TODO(danielhollas): Probably move this into jipt.js:load_phrases()
    // where we can be sure that #translations-dashboard is loaded.
    _inTranslationEditor: function (): boolean {
        return (
            document.querySelectorAll("#translations-dashboard").length === 1
        );
    },

    /**
     * Save suggestions for translations to Crowdin.
     *
     * @param {Array} suggestions An Array of suggestion object which contain
     *      the `phraseId` and `text` which is the translated text to save.
     * @param {Function} [callback] Called after all suggestions are saved.
     */
    saveTranslations: function (
        suggestions: ReadonlyArray<Suggestion>,
        callback?: (() => void) | null,
    ) {
        jipt.save_suggestions(
            suggestions.map((suggestion) => {
                return {
                    translation_id: suggestion.phraseId,
                    plural_num: -1,
                    text: suggestion.text,
                };
            }),
            /* skip crowdin linting: */ 1,
            (data) => {
                callback?.();
                data.forEach((item) => {
                    if (item.success) {
                        const phrase = jipt.phrases[item.translation_id]?.data;

                        if (phrase) {
                            JiptInterface._handleNewTranslation({
                                lang: jipt.language.code,
                                msgid: JiptInterface.maybeUnescape(
                                    phrase.text[0],
                                ),
                                context: phrase.context,
                            });
                        }
                    }
                });
            },
        );
    },

    // Wrapper function for `JiptHack.maybeUnescape()` so that we don't have to
    // expose that file to the rest of the codebase.
    maybeUnescape: function (text: string): string {
        return JiptHack.maybeUnescape(text);
    },

    // Apply escaping to an unescaped translation, but only if the
    // corresponding source string seems to be escaped. This is needed by the
    // "smart translations" system, which always works with unescaped strings,
    // and needs to convert back to Crowdin's format afterwards.
    escapeTranslationIfSourceIsEscaped: function (
        source: string,
        translation: string,
    ): string {
        if (JiptHack.shouldUnescape(source)) {
            return translation.replace(
                /[\n\r\t\\]/g,
                (match) => ESCAPE_SEQUENCES[match],
            );
        }
        return translation;
    },

    /**
     * Object that maps a string returned by _lintKey to a lint
     * result, like the kind returned from
     * /api/internal/_rg/jipt/lint-translations
     */
    _lintCache: {} as any,

    /**
     * Given the components of a phrase, returns a string that uniquely
     * identifies that string. Used for caching lint results. For definitions
     * of these components, see _lint.
     */
    _lintKey: function (
        msgid: string,
        msgstr: string,
        context: string,
    ): string {
        return `${escapeDoubleColon(msgid)}::${escapeDoubleColon(
            msgstr,
        )}::${escapeDoubleColon(context)}`;
    },

    /**
     * Saves a lint string in the cache, given the components of
     * a phrase. For definitions of these components, see _lint.
     *
     * result is the same type that is returned from
     * /api/internal/_rg/jipt/lint-translations
     */
    _recordLint: function (
        msgid: string,
        msgstr: string,
        context: string,
        result: {
            status: string;
            message?: string | null | undefined;
            correction?: string | null | undefined;
        },
    ) {
        const key = JiptInterface._lintKey(msgid, msgstr, context);
        JiptInterface._lintCache[key] = result;
    },

    /**
     * Retreives a lint result from the cache, given the components of
     * a phrase. For definitions of these components, see _lint.
     *
     * If no such component is stored, returns undefined.
     *
     * Thes result returned is the same kind that is returned from
     * /api/internal/_rg/jipt/lint-translations
     */
    _getCachedLint: function (
        msgid: string,
        msgstr: string,
        context: string,
    ): LintResult | null | undefined {
        const key = JiptInterface._lintKey(msgid, msgstr, context);
        return JiptInterface._lintCache[key];
    },

    /**
     * Retreives a lint result from the cache for the given phrase.
     */
    getCachedLintForPhrase: function (
        phrase: Phrase,
    ): LintResult | null | undefined {
        const [msgid, msgstr] = JiptHack.maybeUnescapeAccordingToSource(
            phrase.text[0],
            phrase.translation[0],
        );

        // This will happen if there's a mistake in the translation of
        // an old-style string which makes it impossible to unescape
        if (msgid == null || msgstr == null) {
            return null;
        }

        const context = phrase.context ? tcommentToComment(phrase.context) : "";

        return JiptInterface._getCachedLint(msgid, msgstr, context);
    },

    /**
     * Run our translation linter against the given `msgid` (English string)
     * and `msgstr` (translated string), with the given Crowdin `context`
     * and for the given `language`. The JSON response from the linter API call
     * will be returned. If the API call fails, `propagateError` determines
     * whether `{status: "error"}` (true) or `{status: "ok"}` (false)
     * will be returned.
     *
     * If "callback" is set, this function will be asyncronous, and callback
     * will be called with the lint result. Otherwise, the function will be
     * syncronous, and the lint result will be returned.
     *
     * This function memoizes for performance, so you don't need to worry
     * about manually caching results.
     */
    _lint: function (
        msgid: string,
        msgstr: string,
        context: string,
        language: string = jipt.language.code,
        propagateError = false,
        callback: ((arg1: LintResult) => unknown) | null = null,
    ): LintResult | null | undefined {
        const async = Boolean(callback);

        let result: LintResult | null | undefined =
            msgstr === null
                ? {
                      status: "error",
                      message:
                          "There seems to be a mismatch in the number of " +
                          "backslashes between the English string and the " +
                          "translation",
                  }
                : JiptInterface._getCachedLint(msgid, msgstr, context);

        if (result != null) {
            if (callback) {
                const cbResult = result;
                // For consistency, we'll want to defer this.
                // TODO(jeff, CP-3128): Use Wonder Blocks Timing API
                // eslint-disable-next-line no-restricted-syntax -- there are no other options since this isn't a React component
                setTimeout(() => {
                    callback(cbResult);
                }, 0);
                return;
            }
            return result;
        }

        const lintEntries = [
            createLintTranslationInput(msgid, msgstr, context),
        ];

        // We're using a synchronous XMLHttpRequests here because we need the
        // request to be synchronous to work with Crowdin's JIPT API.
        const xhr = new XMLHttpRequest();
        xhr.open(
            "POST",
            "/api/internal/_rg/jipt/lint-translations?lang=" + language,
            async,
        );
        xhr.setRequestHeader("Content-Type", "application/json");

        const failCallback = () => {
            // TODO(james): See if we can convince crowdin to be able to add
            // an optional "commit anyway" button, then just make this the
            // error message in the return value.
            const message = "Error doing lint check";
            alert(message);
            // Our lint check is broken, hopefully they will complain about
            // the above alert and we can look into it. In the meantime,
            // let them at least still commit. However, we do want to propagate
            // the error in the second lint check for the "mixed escaping".
            if (propagateError) {
                result = {
                    status: "error",
                    message: message,
                };
            } else {
                result = {status: "ok"};
            }
            if (async) {
                callback?.(result);
            }
        };

        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                // Handle successful response here (equivalent to the .then() callback)
                const data = JSON.parse(xhr.response);

                const localResult =
                    data.length > 0
                        ? data[0]
                        : {
                              status: "error",
                              message:
                                  "There was an error with the translation linter",
                          };
                localResult.status = localResult.status.toLowerCase();
                result = localResult;
                JiptInterface._recordLint(msgid, msgstr, context, localResult);
                if (async) {
                    callback?.(localResult);
                }
            } else {
                failCallback();
            }
        };

        xhr.onerror = failCallback;

        // Send the request with the JSON data
        xhr.send(JSON.stringify(lintEntries));

        return async ? undefined : result;
    },

    /**
     * Mark a JIPT phrase as being untranslated, which we do for preexisting
     * translations that contain lint errors. Returns whether the phrase's
     * translation status changed (true if it was previously translated,
     * false otherwise).
     */
    _markAsUntranslated: function (phrase: any): boolean {
        if (!phrase.data.status.translated) {
            return false;
        }
        // This is the default structure for untranslated
        // strings. I'm not sure what the structures for
        // approved and partially_approved represent.
        phrase.data.status = {
            approved: [undefined, undefined, false],
            partially_approved: [undefined, undefined, false],
            partially_translated: false,
            translated: false,
        };
        phrase.kaLintFailed = true; // for styling
        return true;
    },
    /**
     * Call this when the active phrase should be linted.
     * This should be called when the active phrase is changed, or when
     * the active phrase is saved.
     *
     * This emits ka_lint_change, which consumers can subscribe to using
     * addLintChangeListener.
     */
    _updateLintStatus: debounce(
        function _updateLintStatus() {
            const phrase = JiptInterface.getDirtyPhrase();
            if (!phrase) {
                return;
            }
            const newPhraseKey = getPhraseKey(phrase);
            if (lastDirtyPhraseKey === newPhraseKey) {
                return;
            }
            lastDirtyPhraseKey = newPhraseKey;
            lastCleanPhraseKey = getPhraseKey(JiptInterface.getCleanPhrase());

            // Phrase may not have a context string here.
            // @ts-expect-error - TS2345 - Argument of type 'string | null | undefined' is not assignable to parameter of type 'string'.
            const context = tcommentToComment(phrase.context);
            const [msgid, msgstr] = JiptHack.maybeUnescapeAccordingToSource(
                phrase.text[0],
                phrase.translation[0],
            );
            if (msgstr == null) {
                const event = new CustomEvent("ka_lint_change", {
                    detail: {
                        phrase,
                        lintResult: {
                            message:
                                "Could not process backslashes. Check every " +
                                "use of backslashes (\\) in the translation to make " +
                                "sure the same number of backslashes are used as in " +
                                "as the original text.",
                            status: "error",
                        },
                    },
                });
                window.dispatchEvent(event);
                return;
            }

            if (!msgstr.length) {
                // Don't lint if the translation is empty.
                const event = new CustomEvent("ka_lint_change", {
                    detail: {phrase, lintResult: null},
                });
                window.dispatchEvent(event);
                return;
            }

            JiptInterface._lint(
                // @ts-expect-error - TS2345 - Argument of type 'string | null | undefined' is not assignable to parameter of type 'string'.
                msgid,
                msgstr,
                context,
                undefined,
                false,
                (lintResult) => {
                    if (lintResult.correction) {
                        // If we're going to have to unescape the string,
                        // we have to escape it here. This makes the correction
                        // match the escape style of the source (phrase.text[0])
                        lintResult.correction =
                            JiptInterface.escapeTranslationIfSourceIsEscaped(
                                phrase.text[0],
                                lintResult.correction,
                            );
                    }

                    const event = new CustomEvent("ka_lint_change", {
                        detail: {phrase, lintResult},
                    });
                    window.dispatchEvent(event);
                },
            );
        },
        0,
        // @ts-expect-error - TS2769 - No overload matches this call.
        false,
    ) as () => void,

    init: _.once(function () {
        // Crowdin jipt.js may append `window.location` when composing HTML
        // which can be a vector for an XSS attack. We encode the URI fragment
        // if it contains invalid characters. Note that the fragment is expected
        // to contain tutorial slug for topic URI, example:
        // http://translate.khanacademy.org/math/early-math/
        // cc-early-math-counting-topic#cc-early-math-numbers-120
        // TODO(jangmi, CP-3340): Use withLocation to access SSR safe location.
        // eslint-disable-next-line no-restricted-syntax -- there are no other options since this isn't a React component
        if (/[><"']/.test(window.location.hash)) {
            // TODO(jangmi, CP-3340): Use withLocation to access SSR safe location.
            // eslint-disable-next-line no-restricted-syntax -- there are no other options since this isn't a React component
            window.location.hash = encodeURI(window.location.hash);
        }

        // @ts-expect-error - TS2339 - Property '_jipt' does not exist on type 'Window & typeof globalThis'.
        window._jipt = [];
        // @ts-expect-error - TS2339 - Property '_jipt' does not exist on type 'Window & typeof globalThis'.
        window._jipt.push(["project", "khanacademy"]);
        // @ts-expect-error - TS2339 - Property '_jipt' does not exist on type 'Window & typeof globalThis'.
        window._jipt.push(["preload_texts", false]);
        // @ts-expect-error - TS2339 - Property '_jipt' does not exist on type 'Window & typeof globalThis'.
        window._jipt.push([
            "inTranslationEditor",
            JiptInterface._inTranslationEditor(),
        ]);
        // @ts-expect-error - TS2339 - Property '_jipt' does not exist on type 'Window & typeof globalThis'.
        window._jipt.push([
            "before_dom_insert",
            function (
                text: unknown,
                node: unknown,
                attribute: unknown,
                jiptString: unknown,
            ) {
                // We will add functions onto JIPT.domInsertChecks if
                // later on we decide we need for some strings to modify the
                // output ourselves before adding it to the dom.
                _.each(JIPT.domInsertChecks, function (f) {
                    // if text is false at any point then we know we can
                    // tell jipt not to update the node, that we've got
                    // it covered.
                    if (text !== false) {
                        // @ts-expect-error - TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'string'.
                        text = f(text, node, attribute, jiptString);
                    }
                });

                return text;
            },
        ]);
        // @ts-expect-error - TS2339 - Property '_jipt' does not exist on type 'Window & typeof globalThis'.
        window._jipt.push([
            "before_commit",
            function (
                source: unknown,
                translation: unknown,
                context: unknown,
                language: unknown,
            ) {
                // @ts-expect-error - TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'string'.
                context = tcommentToComment(context);
                const [msgid, msgstr] = JiptHack.maybeUnescapeAccordingToSource(
                    // @ts-expect-error - TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'string'.
                    source,
                    translation,
                );

                const lintResult = JiptInterface._lint(
                    // @ts-expect-error - TS2345 - Argument of type 'string | null | undefined' is not assignable to parameter of type 'string'.
                    msgid,
                    msgstr,
                    context,
                    language,
                    false,
                );

                if (lintResult?.["correction"]) {
                    const maybeUnescaped =
                        JiptHack.maybeUnescapeAccordingToSource(
                            // @ts-expect-error - TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'string'.
                            source,
                            lintResult["correction"],
                        );
                    // `maybeUnescapeAccordingToSource()` may return null for the
                    // second element if it cannot be unescaped, but we trust that
                    // the linter's own correction will be valid
                    lintResult["correction"] = maybeUnescaped[1];
                }

                // We just need to report whether lint passes or not.
                // The notification view will give us more details.
                JiptInterface._updateLintStatus();

                if (lintResult?.["status"] !== "ok") {
                    return {
                        status: "error",
                        message: "To save, please correct the above issue.",
                    };
                }

                JiptInterface._handleNewTranslation({
                    // @ts-expect-error - TS2322 - Type 'unknown' is not assignable to type 'string'.
                    lang: language,
                    // @ts-expect-error - TS2322 - Type 'string | null | undefined' is not assignable to type 'string'.
                    msgid: msgid,
                    // @ts-expect-error - TS2322 - Type 'unknown' is not assignable to type 'string'.
                    context: context,
                });

                return {status: "ok"};
            },
        ]);
        // @ts-expect-error - TS2339 - Property '_jipt' does not exist on type 'Window & typeof globalThis'.
        window._jipt.push([
            "ka_after_preview_updated",
            function () {
                const cleanPhraseKey = getPhraseKey(
                    JiptInterface.getCleanPhrase(),
                );
                if (lastCleanPhraseKey !== cleanPhraseKey) {
                    JiptInterface._updateLintStatus();
                }
            },
        ]);

        // We monkey-patch JIPT to lint phrases it receives, and mark phrases
        // that do not pass lint as untranslated.
        // @ts-expect-error - TS2339 - Property '_jipt' does not exist on type 'Window & typeof globalThis'.
        window._jipt.push([
            "ka_on_phrases_received",
            function (phrases: Record<string, WrappedPhrase>) {
                const indexToPhraseId: Record<number, string | number> = {};
                let changedStatus = false;
                const lintEntries: Array<TranslationLinterInput> = [];
                Object.values(phrases).forEach((phrase: WrappedPhrase) => {
                    if (phrase.data.text.length > 1) {
                        // This string has plural forms. The following code
                        // won't correctly handle it and we don't have a way to
                        // correctly lint it, so just skip it.
                        // TODO(emily): Make our linter API and this code
                        // correctly handle plural forms!
                        return;
                    }

                    const [msgid, msgstr] =
                        JiptHack.maybeUnescapeAccordingToSource(
                            phrase.data.text[0],
                            phrase.data.translation[0],
                        );
                    if (msgid != null && msgstr != null) {
                        const context = phrase.data.context
                            ? tcommentToComment(phrase.data.context)
                            : "";
                        indexToPhraseId[lintEntries.length] = phrase.data.id;
                        lintEntries.push(
                            createLintTranslationInput(msgid, msgstr, context),
                        );
                    } else {
                        // This will happen if there's a mistake in the translation
                        // of an old-style string which makes it impossible
                        // to unescape
                        if (JiptInterface._markAsUntranslated(phrase)) {
                            changedStatus = true;
                        }
                    }
                });

                // NOTE(emily): Our API actually errors if we send it an empty
                // list, so we need to bail out early here.
                if (lintEntries.length === 0) {
                    return Promise.resolve();
                }

                return khanFetch(
                    `/api/internal/_rg/jipt/lint-translations?lang=${jipt.language.code}`,
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify(lintEntries),
                    },
                )
                    .then((resp) => resp.json())
                    .then((lintStatuses) => {
                        lintStatuses.forEach((status, idx) => {
                            status.status = status.status.toLowerCase();
                            const {msgid, msgstr, context} = lintEntries[idx];
                            JiptInterface._recordLint(
                                msgid,
                                msgstr,
                                context ?? "",
                                status,
                            );

                            const id = indexToPhraseId[idx];
                            if (status.status !== "ok") {
                                if (
                                    JiptInterface._markAsUntranslated(
                                        phrases[id],
                                    )
                                ) {
                                    changedStatus = true;
                                }
                            }
                        });
                        if (changedStatus) {
                            // Once JIPT is finished processing things, trigger a
                            // manticore refresh.
                            // TODO(jeff, CP-3128): Use Wonder Blocks Timing API
                            // eslint-disable-next-line no-restricted-syntax -- there are no other options since this isn't a React component
                            setTimeout(() => {
                                const event = new Event(
                                    "ka_jipt_status_change",
                                );
                                window.dispatchEvent(event);
                            }, 0);
                        }
                    })
                    .catch((e) => {
                        // TODO(joshuan): Lint the entries individually
                        Log.error(
                            "Failed to lint all the translations: ",
                            Errors.Internal,
                            {cause: e},
                        );
                        alert(
                            "Failed to lint all the translations. There may " +
                                "or may not be lint errors in some of the translations, " +
                                "but they won't be shown until you save the translation.",
                        );
                    });
            },
        ]);

        // This is a **synchronous** hook called when a translation is sent
        // from the Crowdin iframe.
        //
        // Returns true to allow the status of the translation in the UI
        // to become what Crowdin thinks it should be. Return false to keep the
        // existing status.
        // @ts-expect-error - TS2339 - Property '_jipt' does not exist on type 'Window & typeof globalThis'.
        window._jipt.push([
            "ka_on_save",
            function (phrase: unknown) {
                const [msgid, msgstr] = JiptHack.maybeUnescapeAccordingToSource(
                    // @ts-expect-error - TS2571 - Object is of type 'unknown'.
                    phrase.text[0],
                    // @ts-expect-error - TS2571 - Object is of type 'unknown'.
                    phrase.translation[0],
                );

                // This will happen if there's a mistake in the translation of
                // an old-style string which makes it impossible to unescape
                if (msgstr === null) {
                    return false;
                }

                // @ts-expect-error - TS2571 - Object is of type 'unknown'.
                const context = phrase.context
                    ? // @ts-expect-error - TS2571 - Object is of type 'unknown'.
                      tcommentToComment(phrase.context)
                    : "";

                const data = JiptInterface._lint(
                    // @ts-expect-error - TS2345 - Argument of type 'string | null | undefined' is not assignable to parameter of type 'string'.
                    msgid,
                    msgstr,
                    context,
                    jipt.language.code,
                );

                const ok = data?.status === "ok";

                // @ts-expect-error - TS2571 - Object is of type 'unknown'.
                phrase.kaLintFailed = !ok; // for styling

                return ok;
            },
        ]);

        jipt.init();

        return new Promise<void>((resolve) => {
            // eslint-disable-next-line no-restricted-syntax -- there are no other options since this isn't a React component
            const interval = setInterval(() => {
                if (jipt.editor_loaded) {
                    // eslint-disable-next-line no-restricted-syntax -- there are no other options since this isn't a React component
                    clearInterval(interval);
                    resolve();
                }
            }, 100);
        });
    }) as () => Promise<void>,
} as const;

export default JiptInterface;
